// Copyright (c) Docugami, Inc. All rights reserved.

import { Modal } from "@fluentui/react";
import { supportedExtensions } from "components/file_management/file_helpers";
import { ContentLoadState, ContentType, useDynamicHtmlContent } from "hooks/DynamicHtmlContent";
import { BoxItemType, ProviderId } from "networking/StorageProviders";
import { useState } from "react";
import { useEffect, useRef } from "react";
import styled from "styled-components";
import { TelemetryExceptions } from "telemetry/TelemetryEvents";
import { Telemetry } from "telemetry/TelemetryManager";
import { FilePickerProps, ItemTypeFilter, SelectionMode } from "./StorageProviderFilePicker";
import { ProviderPickerItem } from "./ProviderPickerItem";

const BoxPickerContainer = styled.div`
    height: 600px;
    max-height: 60%;
    min-width: 800px;
    max-width: 75%;
`

interface BoxPickerItem {
    id: string;
    etag: string;
    name: string;
    type: string;
    extension?: string;
    path_collection: PathCollection;
}

interface PathCollectionEntry {
    type: string;
    id: string;
    sequence_id: string | null;
    etag: string;
    name: string;
}

interface PathCollection {
    entries: PathCollectionEntry[];
    total_count: number;
}

interface BoxPickerProps extends FilePickerProps {
}

interface PickerState {
    isReady?: boolean;
    isError?: boolean;
}

export const BoxPicker = (props: BoxPickerProps): JSX.Element => {
    const { onCancel, onConfirm, token, selectionMode, itemTypeFilter } = props;
    const onConfirmRef = useRef<(items: ProviderPickerItem[]) => void>(onConfirm);
    const onCancelRef = useRef<() => void>(onCancel);

    const [state, setState] = useState<PickerState>({});

    const boxPickerRef = useRef<any>();
    const lifetimeRef = useRef<boolean>(true);

    const boxStyle = useDynamicHtmlContent(ContentType.StyleSheet, "https://cdn01.boxcdn.net/platform/elements/13.0.0/en-US/picker.css")
    const boxScript = useDynamicHtmlContent(ContentType.Script, "https://cdn01.boxcdn.net/platform/elements/13.0.0/en-US/picker.js")

    useEffect(() => {
        return () => {
            lifetimeRef.current = false;
            boxPickerRef.current?.removeAllListeners();
            boxPickerRef.current = null;

            if (!!onCancelRef.current) {
                onCancelRef.current();
            }
        }
    }, []);

    useEffect(() => {
        onConfirmRef.current = onConfirm;
    }, [onConfirm]);

    useEffect(() => {
        onCancelRef.current = onCancel;
    }, [onCancel]);

    useEffect(() => {
            setState({
                isReady: boxScript === ContentLoadState.Ready && boxScript === ContentLoadState.Ready,
                isError: boxScript === ContentLoadState.Error || boxStyle === ContentLoadState.Error
            });
    }, [boxStyle, boxScript]);

    useEffect(() => {
        if (!!state.isReady) {
            if (props.isOpen) {
                boxPickerRef.current?.removeAllListeners();

                // @ts-ignore
                boxPickerRef.current = new Box.ContentPicker();

                boxPickerRef.current.addListener('cancel', () => onCancelRef.current());
                boxPickerRef.current.addListener('choose', (items: BoxPickerItem[]) => {
                    items = items.filter(x => x.type === 'folder' || (x.type === 'file' && !!x.extension && !!supportedExtensions.find(e => e.includes(x.extension ?? ''))));
                    const pickerItems = items.map<ProviderPickerItem>(x => {
                        const type = convertItemType(x.type);
                        return {
                            item: {
                                id: x.id,
                                type: type
                            },
                            path: getPath(x),
                            name: x.name,
                            isFolder: type === BoxItemType.Folder,
                            providerId: ProviderId.Box
                        }
                    });

                    onConfirmRef.current(pickerItems);
                });

                let extensions: string[] = [];
                const fileExtensions = supportedExtensions.map(x => x.substring(1));
                const folderExtensions = [''];

                switch (itemTypeFilter) {
                    case ItemTypeFilter.All:
                        extensions = [...fileExtensions, ...folderExtensions];
                        break;
                    case ItemTypeFilter.File:
                        extensions = fileExtensions;
                        break;
                    case ItemTypeFilter.Folder:
                        extensions = folderExtensions;
                        break;
                }

                const options: any = {
                    container: '.dgBoxPicker',
                    logoUrl: 'box',
                    canUpload: false,
                    canSetShareAccess: false,
                    chooseButtonLabel: "Confirm",
                    canCreateNewFolder: false,
                    extensions: extensions,
                    maxSelectable: selectionMode === SelectionMode.Single ? 1 : Infinity
                }

                setTimeout(() => {
                    // This is an unfortunate hack to work around a bug in the BoxPicker.js code that attempts to render a DOMNode
                    // before it exists in the tree
                    if (lifetimeRef.current) {
                        boxPickerRef.current?.show("0", token.value, options)
                    }
                }, 50);
            }
        }
    }, // eslint-disable-next-line react-hooks/exhaustive-deps
        [props.isOpen, state, token]);

    useEffect(() => {
        if (!!state.isError) {
            Telemetry.trackExceptionEvent(TelemetryExceptions.BoxPickerLoadFailed);
            onCancel();
        }
    }, [state, onCancel]);

    return (
        <Modal
            isOpen={props.isOpen}
            isBlocking={true}
            styles={{
                main: {
                    borderRadius: "10px"
                },
            }}>
            <BoxPickerContainer className="dgBoxPicker"></BoxPickerContainer>
        </Modal>
    );
}

function convertItemType(type: string): BoxItemType {
    switch (type) {
        case "file":
            return BoxItemType.File;
        case "folder":
            return BoxItemType.Folder;
        default:
            throw new Error("Unsupported item type.")
    }
}

function getPath(boxItem: BoxPickerItem): string {
    return [...boxItem.path_collection.entries.filter(x => x.sequence_id != null).map(x => x.name), boxItem.name].join('/');
}
